import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface AppNotification {
  id: string;
  user_id: string;
  event_type: string;
  title: string;
  body: string;
  icon: string | null;
  priority: 'critical' | 'standard' | 'info';
  action_url: string | null;
  action_label: string | null;
  is_read: boolean;
  is_archived: boolean;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('notifications')
      .select('id,user_id,event_type,title,body,icon,priority,action_url,action_label,is_read,is_archived,created_at')
      .eq('user_id', user.id)
      .eq('is_archived', false)
      .order('created_at', { ascending: false })
      .limit(30);
    setItems((data || []) as AppNotification[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) return;
    const ch = supabase.channel(`notifications-${user.id}-${Math.random().toString(36).slice(2)}`);
    ch.on(
      'postgres_changes' as any,
      { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
      () => load(),
    ).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, load]);

  const unread = items.filter(i => !i.is_read).length;

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true, read_at: new Date().toISOString() }).eq('id', id);
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_read: true } : i));
  };
  const markAllRead = async () => {
    if (!user) return;
    await supabase.from('notifications').update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', user.id).eq('is_read', false);
    setItems(prev => prev.map(i => ({ ...i, is_read: true })));
  };

  return { items, unread, loading, markRead, markAllRead, reload: load };
}
